import React, { useState, useEffect } from "react";
import { SectionWrapper } from "../components/SectionWrapper";
import { Navbar } from "../components/Navbar";
import { Skills } from "../pages/Skills";
import { LandingSection } from "../pages/LandingSection";
import { About } from "../pages/About";
import { InitialLoader } from "../pages/InitialLoader";
import { motion } from "motion/react";
import { ChatbotDialog } from "../components/ChatbotDialog";
import { useCurrentChatSession } from "../utils/useCurrentSession";
import { Button } from "@mui/material";
import { SiOpenaigym } from "react-icons/si";
import { useResponsive } from "../utils/responsiveContext";
import { checkServerActive } from "../utils/api";
import { Experience } from "../pages/Experience";
import { Timeline } from "../pages/Timeline";
import { Contact } from "../pages/Contact";

export const Layout = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showLoader, setShowLoader] = useState(true);
  const [isChatbotDialogOpen, setIsChatbotDialogOpen] = useState(false);
  const { isServerActive, setIsServerActive } = useCurrentChatSession();
  const { isMobile } = useResponsive();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 4500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let intervalId;

    const checkServer = async () => {
      try {
        const response = await checkServerActive();
        if (response) {
          setIsServerActive(true);
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error("Error checking server status:", err);
      }
    };

    checkServer();

    if (!isServerActive) {
      intervalId = setInterval(checkServer, 30 * 1000);
    }

    return () => clearInterval(intervalId);
  }, [isServerActive, setIsServerActive]);

  const sections = [
    { title: "home()", content: <LandingSection /> },
    { title: "about()", content: <About /> },
    { title: "skills()", content: <Skills /> },
    { title: "experience()", content: <Experience /> },
    { title: "timeline()", content: <Timeline /> },
    { title: "contact()", content: <Contact /> },
  ];

  return (
    <div className="w-screen h-screen text-shade-4 bg-shade-0">
      {showLoader && (
        <motion.div
          initial={{ y: 0, opacity: 1 }}
          animate={
            isLoading ? { y: 0, opacity: 1 } : { y: "-100%", opacity: 0 }
          }
          transition={{ duration: 1, ease: "easeInOut" }}
          onAnimationComplete={() => !isLoading && setShowLoader(false)}
          className="absolute inset-0 z-50"
        >
          <InitialLoader />
        </motion.div>
      )}
      {!showLoader && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={isLoading ? { opacity: 0, y: 100 } : { opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeInOut" }}
        >
          <Navbar />
          <div className="relative w-full h-full">
            {sections.map((section, index) => (
              <SectionWrapper key={index} title={section.title}>
                {section.content}
              </SectionWrapper>
            ))}
          </div>
        </motion.div>
      )}
      {!showLoader && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 2, duration: 0.5 }}
          className={`fixed z-50 ${
            isMobile ? "bottom-5 right-5" : "bottom-10 right-10"
          }`}
        >
          {isServerActive ? (
            <motion.div
              whileHover={{
                scale: 1.2,
                borderRadius: "10px",
                boxShadow:
                  "rgba(240, 46, 170, 0.6) 3.46px 1px,rgba(46, 240, 170, 0.5) 6.93px 2px,rgba(170, 46, 240, 0.4) 10.39px 3px,rgba(240, 170, 46, 0.3) 13.86px 4px, rgba(46, 170, 240, 0.2) 17.32px 5px",
              }}
              className="cursor-pointer"
              onClick={() => setIsChatbotDialogOpen(true)}
            >
              <Button
                disableRipple
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                className="!px-5 !py-3 !rounded-xl !bg-shade-1 animate-border-pulse"
              >
                Ask AI Agent
                <SiOpenaigym className="size-4 ml-2" />
              </Button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0.7 }}
              whileHover={{
                scale: 1.05,
              }}
              className="cursor-not-allowed"
            >
              <Button
                className="!text-shade-4 !px-5 !py-3 !rounded-xl !border !border-shade-4 !bg-shade-1 blink-button"
                variant="outlined"
                loading={true}
                size={isMobile ? "small" : "medium"}
                loadingPosition="start"
              >
                Waking up AI Agent...
              </Button>
            </motion.div>
          )}
        </motion.div>
      )}

      <ChatbotDialog
        isOpen={isChatbotDialogOpen}
        setIsChatbotDialogOpen={setIsChatbotDialogOpen}
      />
    </div>
  );
};
